import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import bbox from '@turf/bbox';
import { featureCollection, point } from '@turf/helpers';
import { useMemo } from 'react';

import { Line, Marker } from '../components/map/types';
import { getCurvedLineCoordinates } from '../components/map/utils';
import { getStepForTrip, TripStep } from '../utils/trip';

const driverSteps: TripStep[] = ['DRIVER_APPROACHING', 'DRIVER_PREVIOUS_DROP_OFF', 'DRIVER_IN_PLACE', 'ONDUTY'];

export const useTripMapFeatures = (trip: TripOutput) => {
  const step = getStepForTrip(trip);

  const markers = useMemo<Marker[]>(() => {
    const start = trip.resume.pickUpPoint
      ? { lat: trip.resume.pickUpPoint.lat, lng: trip.resume.pickUpPoint.lng }
      : { lat: trip.resume.start.lat, lng: trip.resume.start.lng };

    const startMarker: Marker = {
      type: 'start',
      lat: start.lat,
      lng: start.lng,
    };
    const endMarker: Marker = { type: 'end', lat: trip.resume.end.lat, lng: trip.resume.end.lng };
    const stepsMarkers: Marker[] = (trip.resume.steps ?? []).map(step => ({
      type: 'step',
      lat: step.lat,
      lng: step.lng,
    }));
    const previousDropOffMarker: Marker | undefined =
      step === 'DRIVER_PREVIOUS_DROP_OFF' && trip.driver?.previousTripDropoffLat && trip.driver?.previousTripDropoffLng
        ? { type: 'previousDropOff', lat: trip.driver.previousTripDropoffLat, lng: trip.driver.previousTripDropoffLng }
        : undefined;
    const driverMarker: Marker | undefined =
      trip.driver?.lat && trip.driver?.lng && driverSteps.includes(step)
        ? { type: 'driver', lat: trip.driver.lat, lng: trip.driver.lng }
        : undefined;

    return [
      startMarker,
      ...stepsMarkers,
      endMarker,
      ...(previousDropOffMarker ? [previousDropOffMarker] : []),
      ...(driverMarker ? [driverMarker] : []),
    ];
  }, [
    step,
    trip.driver?.lat,
    trip.driver?.lng,
    trip.driver?.previousTripDropoffLat,
    trip.driver?.previousTripDropoffLng,
    trip.resume.end.lat,
    trip.resume.end.lng,
    trip.resume.pickUpPoint,
    trip.resume.start.lat,
    trip.resume.start.lng,
    trip.resume.steps,
  ]);

  const [minX, minY, maxX, maxY] = useMemo(() => {
    let filteredMarkers = markers.filter(marker => marker.type !== 'driver');

    if (step === 'DRIVER_APPROACHING' || step === 'DRIVER_PREVIOUS_DROP_OFF' || step === 'DRIVER_IN_PLACE') {
      filteredMarkers = markers.filter(
        marker => marker.type === 'driver' || marker.type === 'start' || marker.type === 'previousDropOff',
      );
    }

    if (step === 'ONDUTY') {
      filteredMarkers = markers.filter(
        marker => marker.type === 'driver' || marker.type === 'step' || marker.type === 'end',
      );
    }

    return bbox(featureCollection(filteredMarkers.map(marker => point([marker.lng, marker.lat]))));
  }, [markers, step]);

  const paths = useMemo<Line[]>(() => {
    if (step === 'DRIVER_APPROACHING' && trip.driver?.lat && trip.driver.lng) {
      return [
        {
          id: 'driver-approach',
          color: '#FF8400',
          width: 4,
          coordinates: getCurvedLineCoordinates(
            [trip.driver.lng, trip.driver.lat],
            [trip.resume.start.lng, trip.resume.start.lat],
          ),
        },
      ];
    }

    if (
      step === 'DRIVER_PREVIOUS_DROP_OFF' &&
      trip.driver?.previousTripDropoffLat &&
      trip.driver?.previousTripDropoffLng
    ) {
      return [
        {
          id: 'driver-approach',
          color: '#FF8400',
          width: 4,
          coordinates: getCurvedLineCoordinates(
            [trip.driver.previousTripDropoffLng, trip.driver.previousTripDropoffLat],
            [trip.resume.start.lng, trip.resume.start.lat],
          ),
        },
      ];
    }

    if (step === 'DRIVER_IN_PLACE') {
      return [];
    }

    if (step === 'ONDUTY' && trip.driver?.lat && trip.driver.lng) {
      return [
        {
          id: 'trip-route',
          color: '#FF8400',
          width: 4,
          coordinates: getCurvedLineCoordinates(
            [trip.driver.lng, trip.driver.lat],
            [trip.resume.end.lng, trip.resume.end.lat],
          ),
        },
      ];
    }

    return [
      {
        id: 'trip-route',
        color: '#FF8400',
        width: 4,
        coordinates: getCurvedLineCoordinates(
          [trip.resume.start.lng, trip.resume.start.lat],
          [trip.resume.end.lng, trip.resume.end.lat],
        ),
      },
    ];
  }, [
    step,
    trip.driver?.lat,
    trip.driver?.lng,
    trip.driver?.previousTripDropoffLat,
    trip.driver?.previousTripDropoffLng,
    trip.resume.end.lat,
    trip.resume.end.lng,
    trip.resume.start.lat,
    trip.resume.start.lng,
  ]);

  return { markers, bounds: [minX, minY, maxX, maxY] as [number, number, number, number], paths };
};
